import axios from 'axios'
import { auth } from '@/firebase/firebaseConfig'

const DEFAULT_TIMEOUT = 10_000
const UPLOAD_TIMEOUT  = 30_000
const MAX_RETRIES     = 2

const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
})

export const apiUpload = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: UPLOAD_TIMEOUT,
})

async function attachToken(config) {
  const user = auth.currentUser
  if (user) {
    const token = await user.getIdToken(false)
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
}

api.interceptors.request.use(attachToken)
apiUpload.interceptors.request.use(attachToken)


function isRetryable(error) {
  const status    = error.response?.status
  const isTimeout = error.code === 'ECONNABORTED'
  const isNetwork = !error.response
  if (error.config?._noRetry) return false

  // Só retenta métodos seguros (GET) — reenviar POST/PUT/PATCH/DELETE depois
  // de um timeout é perigoso: a requisição original pode já ter chegado no
  // servidor e estar em andamento (ou até já ter concluído), e reenviar cria
  // uma segunda transação concorrente sobre o mesmo recurso. Foi exatamente
  // isso que causou um deadlock real no MySQL num PATCH de revisão de cadastro
  // — duas transações batendo na mesma linha ao mesmo tempo.
  const method = (error.config?.method || 'get').toLowerCase()
  if (method !== 'get') return false

  return isTimeout || isNetwork || status === 503
}

api.interceptors.response.use(
  (response) => response,

  async (error) => {
    const config = error.config

    if (isRetryable(error) && (config._retryCount || 0) < MAX_RETRIES) {
      config._retryCount = (config._retryCount || 0) + 1
      const delay = Math.pow(2, config._retryCount - 1) * 1000
      await new Promise(r => setTimeout(r, delay))
      if (process.env.NODE_ENV !== 'production') {
        console.warn(`[api] Retry ${config._retryCount}/${MAX_RETRIES}: ${config.url}`)
      }
      return api(config)
    }

    const status = error.response?.status

    if (status === 401) {
      auth.signOut()
      window.location.href = '/login'
      return Promise.reject(error)
    }

    // Conta banida detectada durante sessão ativa (fora do fluxo de login)
    // O login trata diretamente em useAuth.js para exibir o modal sem redirect.
    // Aqui cobrimos requisições feitas após o login (sessão interceptada).
    if (status === 422 && error.response?.data?.code === 'ACCOUNT_BANNED') {
      const rawReason = error.response?.data?.reason || 'Sua conta foi banida.'
      const safeReason = String(rawReason).replace(/<[^>]*>/g, '').trim().slice(0, 300)
      sessionStorage.setItem('aerj_banned_reason', safeReason || 'Sua conta foi banida.')
      await auth.signOut()
      window.location.href = '/login?banned=1'
      return Promise.reject(error)
    }

    if (status === 403 && error.response?.data?.code === 'STUDENT_INADIMPLENTE') {
      window.dispatchEvent(new CustomEvent('aerj:inadimplente'))
      return Promise.reject(error)
    }

    if (error.code === 'ECONNABORTED') {
      error.isTimeout    = true
      error.userMessage  = 'O servidor demorou demais para responder. Tente novamente.'
    }

    if (!error.response && error.message === 'Network Error') {
      error.isOffline   = true
      error.userMessage = 'Sem conexão com a internet. Verifique sua rede.'
    }

    return Promise.reject(error)
  }
)

export default api
